import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CuttingService, ISetup } from '@shared/services/cutting.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-update-setup-dialog',
	templateUrl: './create-update-setup.html',
	styleUrls: ['./create-update-setup.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateSetupDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	form: FormGroup;
	setup: ISetup;
	httpError = '';
	loading = false;
	cuttingMethodTranslations = [{ type: { self: '/cutting/setup-cutting-methods/edge-to-edge' } }];

	sortingStrategyTranslation = [
		{
			type: {
				self: '/cutting/setup-sorting-strategies/length-ascending',
			},
		},
		{
			type: {
				self: '/cutting/setup-sorting-strategies/length-descending',
			},
		},
	];

	constructor(
		@Inject(MAT_DIALOG_DATA) public editSetup: ISetup,
		public dialogRef: MatDialogRef<CreateUpdateSetupDialogComponent>,
		private cuttingService: CuttingService,
		private fb: FormBuilder,
	) {
		this.setup = {
			self: null,
			name: null,
			description: null,
			updatedDate: null,
			updatedBy: null,
			minLength: null,
			maxLength: null,
			cuttingMethod: null,
			cuttingMethodTranslation: null,
			kerf: null,
			clearance: null,
			rear: null,
			angle: null,
			oversizing: null,
			sortingStrategy: null,
			sortingStrategyTranslation: null,
		};

		if (editSetup) {
			this.setup = { ...editSetup };
		}
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			name: [this.setup.name, [Validators.required, Validators.maxLength(50), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			description: [this.setup.description, [Validators.maxLength(100), Validators.pattern(ValidatorPatterns.DanishCharacters)]],
			minLength: [this.setup.minLength, [Validators.min(0), Validators.max(2147483647)]],
			maxLength: [this.setup.maxLength, [Validators.min(0), Validators.max(2147483647)]],
			kerf: [this.setup.kerf, [Validators.min(0), Validators.max(2147483647)]],
			clearance: [this.setup.clearance, [Validators.min(0), Validators.max(2147483647)]],
			rear: [this.setup.rear, [Validators.min(0), Validators.max(2147483647)]],
			cuttingMethod: [this.setup.cuttingMethod, [Validators.required]],
			sortingStrategy: [this.setup.sortingStrategy, [Validators.required]],
		});

		if (this.cuttingMethodTranslations.length === 1) {
			const singleMethod = this.cuttingMethodTranslations[0];
			this.form.controls['cuttingMethod'].setValue(this.getLastSegment(singleMethod.type.self));
		}

		if (this.sortingStrategyTranslation.length === 1) {
			const singleMethod = this.sortingStrategyTranslation[0];
			this.form.controls['sortingStrategy'].setValue(this.getLastSegment(singleMethod.type.self));
		}

		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.setup) {
					this.setup[key] = value[key];
				}
			});
		});
	}

	get controls() {
		return this.form.controls;
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	async onSave(): Promise<void> {
		this.httpError = '';
		if (this.form.invalid || this.loading) return;
		this.loading = true;

		const saveObservable = this.setup.self ? this.cuttingService.updateSetup(this.setup) : this.cuttingService.createSetup(this.setup);

		saveObservable.subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				this.loading = false;
			},
		});
	}

	getLastSegment(url: string): string {
		return this.cuttingService.getLastSegment(url);
	}
}
